import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

/**
 * UtilsService
 * Provides reusable functions used in different controllers
 */
@Injectable()
export class UtilsService {
  /** Holds behaviour for a switch that changes between table and chart views */
  switchViewMob: BehaviorSubject<boolean>;

  /** Inits switchViewMob as new instance of BehaviourSubject */
  constructor() {
    this.switchViewMob = new BehaviorSubject<boolean>(true);
  }

  /**
   * Transforms text in lowercase and strips some special Spanish characters
   * @param {string} text - Text to be transformed
   */
  stripSpecialCharacters(text: string): string {
    const specialChars  = ['á', 'é', 'í', 'ó', 'ú', 'ü'];
    const strippedChars = ['a', 'e', 'i', 'o', 'u', 'u'];
    text = text.toLowerCase();
    for (let index = 0; index < specialChars.length; index++) {
      const specialChar = specialChars[index];
      const strippedChar = strippedChars[index];
      text = _.replace(text, new RegExp(specialChar, 'g'), strippedChar);
    }
    return text;
  }

  /**
   * Returns url string with encoded special characters
   * @param {string} url - Url string to encode
   */
  encodeUrl(url: string): string {
    return url.split('').map(char => {
      switch (char) {
        case '?':
          return '%3F';
        case '&':
          return '%26';
        case '=':
          return '%3D';
        case '#':
          return '%23';
        default:
          return char;
      }
    }).join('');
  }

  /**
   * Returns the value of switchViewMob as an observable
   */
  getSwitchViewMobile(): Observable<boolean> {
    return this.switchViewMob.asObservable();
  }

  /**
   * Set the value of switchViewMob
   * @param newValue Value to be setted
   */
  setTheBoolean(newValue: boolean): void {
    this.switchViewMob.next(!newValue);
  }

  /**
   * Return an array of categorical selected palettes (safe colors)
   */
  getCategoricalColors(): string[][] {
    return [
      ['#5F4690', '#1E6996', '#38A6A5', '#108554', '#73AF47', '#D81D09', '#FFB11B', '#E17C06', '#CC503E', '#94346E', '#54284E'],
      ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a', '#ffff99'],
      ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd', '#ccebc5'],
      ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33', '#a65628', '#f781bf', '#999999',
      '#FFB11B', '#68deff']
    ];
  }

  /**
   * Returns an array of sequential color palettes (safe colors)
   */
  getSequentialColors(): string[][] {
    return [
      ['#1F2562', '#364B8F', '#5783B3', '#7EB8CF', '#C2DECB'],
      ['#7a0177', '#c51b8a', '#f768a1', '#fbb4b9', '#feebe2'],
      ['#006d2c', '#31a354', '#74c476', '#bae4b3', '#edf8e9'],
      ['#b30000', '#e34a33', '#fc8d59', '#fdcc8a', '#fef0d9'],
      ['#252525', '#636363', '#969696', '#cccccc', '#f7f7f7'],
    ];
  }

  /** Returns if current userAgent is a mobile device */
  isMobile(): boolean {
    let isMobile = false; // initiate as false
    // device detection
    if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))) {
      isMobile = true;
    }
    return isMobile;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/utils/utils.service.ts